/**
 * Per-module actionable checklists. Static task definitions keyed by module slug.
 * User state (which tasks are checked) is persisted to the backend via
 * PUT /api/modules/{slug}/checklist  { task_id, checked }.
 */

export const MODULE_CHECKLISTS = {
  "company-legal-setup": [
    { id: "entity",    label: "Decide entity type (LLC vs. Delaware C-Corp)" },
    { id: "formation", label: "File formation documents with your state" },
    { id: "ein",       label: "Apply for and receive your EIN" },
    { id: "bank",      label: "Open a dedicated business bank account" },
    { id: "founder",   label: "Draft founder / operating / shareholder agreement with vesting" },
    { id: "tm-search", label: "Run a trademark clearance search on your brand name" },
    { id: "tm-file",   label: "File trademark application in the correct class(es)" },
    { id: "gl-ins",    label: "Bind general liability insurance" },
    { id: "pl-ins",    label: "Bind product liability insurance (needed for retailers)" },
    { id: "compliance",label: "Confirm category compliance (FDA / TTB / Prop 65 as applicable)" },
  ],
  "product-development": [
    { id: "concept",   label: "Write a one-sentence product concept + who it's for" },
    { id: "brief",     label: "Complete the formulation brief for your formulator" },
    { id: "prototype", label: "Approve 3+ formulation prototypes" },
    { id: "unit-econ", label: "Model unit economics — COGS ≤ 30% of MSRP target" },
    { id: "packaging", label: "Select primary packaging + confirm co-packer compatibility" },
    { id: "copacker",  label: "Vet and select a co-packer (SQF/BRC + reference calls)" },
    { id: "stability", label: "Run accelerated + real-time stability study" },
    { id: "pilot-run", label: "Complete a small validation production run" },
    { id: "sensory",   label: "Collect blind taste-test feedback from 20+ target customers" },
    { id: "moq-model", label: "Model MOQ and scale-up economics before committing to a large run" },
  ],
  "brand-strategy": [
    { id: "target",    label: "Define your primary target consumer (one wedge, not 'everyone')" },
    { id: "position",  label: "Write your positioning statement (who / what / why us)" },
    { id: "story",     label: "Draft your brand story in 150 words" },
    { id: "identity",  label: "Approve logo, color palette, typography, and packaging system" },
    { id: "front-pack",label: "Confirm front-of-pack communicates category + benefit in <3 seconds" },
    { id: "voice",     label: "Codify brand voice / tone-of-voice guide" },
    { id: "touchpoints",label: "Audit brand consistency across web, pack, DTC, social, retail" },
    { id: "compete",   label: "List 3 competitors + your differentiator vs each" },
    { id: "shelf-test",label: "On-shelf test: place mockups on a real category shelf" },
  ],
  "packaging-compliance": [
    { id: "nfp",       label: "Finalize Nutrition Facts Panel from validated formula" },
    { id: "ingredients",label: "Confirm ingredient statement matches final formulation" },
    { id: "allergens", label: "Add all required allergen callouts" },
    { id: "prop65",    label: "Complete Prop 65 review (if selling in California)" },
    { id: "claims",    label: "Verify every health/functional claim is substantiated" },
    { id: "upc",       label: "Generate GS1-registered UPC barcode and place per retailer specs" },
    { id: "artwork",   label: "Compliance review of final artwork before printing" },
    { id: "materials", label: "Confirm packaging materials meet freight and retailer specs" },
    { id: "case-pack", label: "Approve case pack, master case, and pallet configuration" },
    { id: "sample",    label: "Order printer proofs and physically inspect before full run" },
  ],
  "website-digital-presence": [
    { id: "domain",    label: "Secure the primary .com domain (plus .co / .shop as needed)" },
    { id: "homepage",  label: "Homepage clearly answers: what, for whom, why different — in <5 seconds" },
    { id: "product-pg",label: "Product page with benefits, images, reviews, and add-to-cart" },
    { id: "mobile",    label: "Optimize entire site for mobile (>60% of purchases)" },
    { id: "speed",     label: "Page speed under 3 seconds (Lighthouse ≥ 85)" },
    { id: "email",     label: "Set up email capture with welcome flow (Klaviyo / Sendinblue)" },
    { id: "analytics", label: "Install GA4 + Meta Pixel + conversion tracking" },
    { id: "trust",     label: "Add reviews, media logos, and About Us / Founder story" },
    { id: "checkout",  label: "Streamline checkout (one-page, Apple Pay, Shop Pay)" },
    { id: "seo",       label: "Basic on-page SEO (title tags, meta, alt text, sitemap)" },
  ],
  "social-media-brand-growth": [
    { id: "channels",  label: "Pick 1-2 primary platforms where your target consumer lives" },
    { id: "handles",   label: "Claim consistent handles across every major platform" },
    { id: "content",   label: "Build a content pillar plan (educate / entertain / sell)" },
    { id: "cadence",   label: "Commit to a realistic posting cadence you can actually sustain" },
    { id: "founder",   label: "Publish 3 founder-led pieces (POV, origin, behind-the-scenes)" },
    { id: "ugc",       label: "Run a UGC gifting program with 5-10 micro-influencers" },
    { id: "email-list",label: "Migrate your best social followers onto your email list" },
    { id: "measure",   label: "Track engagement rate + conversion — not just follower count" },
    { id: "listen",    label: "Respond to every comment / DM within 24h for the first 90 days" },
  ],
  "retail-sales-distribution": [
    { id: "channel",   label: "Decide your first channel (independent, natural, grocery, C-store, DTC)" },
    { id: "target",    label: "Build your list of top 20 target retailers" },
    { id: "sellsheet", label: "Create a one-page sell sheet with market data + case story" },
    { id: "pricing",   label: "Set wholesale price + promo ladder + slotting reserves" },
    { id: "broker",    label: "Interview 3 brokers with target-retailer relationships in your category" },
    { id: "coi",       label: "Get a Certificate of Insurance ready to name retailers as additional insured" },
    { id: "pitch",     label: "Pitch 5 retail buyers this month" },
    { id: "velocity",  label: "Track velocity (units/store/week) for every new placement" },
    { id: "promo",     label: "Plan launch promo support (demo, endcap, TPR) for first 60 days" },
  ],
  "manufacturing-supply-chain": [
    { id: "spec",      label: "Finalize product spec sheet (ingredients, process, QC, packaging)" },
    { id: "copack-2",  label: "Line up a backup co-packer (single-source risk is the #1 killer)" },
    { id: "sop",       label: "Document SOPs for production, QC, and finished-goods release" },
    { id: "supplier",  label: "Qualify at least 2 sources for every critical ingredient" },
    { id: "safety",    label: "Confirm co-packer certifications (SQF / BRC / kosher / organic as needed)" },
    { id: "audit",     label: "Complete an on-site audit of your co-packer before production" },
    { id: "batch",     label: "Define batch record + lot-code traceability system" },
    { id: "moq",       label: "Negotiate MOQ + ramp schedule that matches your working capital" },
    { id: "forecast",  label: "Build a 12-week rolling production forecast you actually update" },
  ],
  "logistics-3pl": [
    { id: "3pl",       label: "Select a 3PL (retail-experienced, ideally food-grade if applicable)" },
    { id: "wms",       label: "Confirm the 3PL's WMS integrates with your ecommerce / EDI" },
    { id: "sla",       label: "Sign SLA covering OTIF, order accuracy, and chargebacks" },
    { id: "edi",       label: "Set up EDI for every retailer that requires it" },
    { id: "labels",    label: "Confirm retailer-specific pallet + case labeling" },
    { id: "insurance", label: "Cargo insurance in place for freight" },
    { id: "multi-node",label: "Evaluate a second warehouse node once you cross ~$1M revenue" },
    { id: "otif",      label: "Track OTIF weekly — target 98%+" },
    { id: "forecast-l",label: "Weekly inventory review vs. sell-through to prevent stockouts" },
  ],
  "accounting-finance": [
    { id: "books",     label: "Set up bookkeeping (QuickBooks / Xero) — separate from personal" },
    { id: "cpa",       label: "Retain a CPA who knows CPG (trade spend, freight, promo accruals)" },
    { id: "cogs",      label: "Model true COGS including freight-in, labor, waste, and pallets" },
    { id: "cm",        label: "Model contribution margin per SKU per channel" },
    { id: "cash",      label: "Build a 13-week rolling cash-flow forecast" },
    { id: "ap-ar",     label: "Track AR aging (retailers) and negotiate AP terms with suppliers" },
    { id: "budget",    label: "Set an annual operating budget with monthly variance review" },
    { id: "kpi",       label: "Publish a weekly one-page KPI dashboard (revenue, GM, cash, velocity)" },
    { id: "break-even",label: "Know your break-even volume and current run-rate distance to it" },
  ],
  // Bonus modules — leaner but still meaningful
  "ai-tools": [
    { id: "stack",     label: "Set up your AI stack (ChatGPT/Claude for writing, Notion AI, Fal/Ideogram for visuals)" },
    { id: "voice",     label: "Codify your brand voice into a reusable AI prompt template" },
    { id: "sales",     label: "Use AI to draft your first retail sell sheet, then edit for accuracy" },
    { id: "content",   label: "Generate 30 days of on-brand social captions in one sprint" },
    { id: "review",    label: "Never publish AI output without a human accuracy + brand review" },
  ],
  "broker-masterclass": [
    { id: "shortlist", label: "Shortlist 5 brokers in your category with target-retailer relationships" },
    { id: "refs",      label: "Reference-check every broker with 3 existing brands" },
    { id: "agreement", label: "Sign a broker agreement with clear territories + KPIs" },
    { id: "materials", label: "Equip broker with sell sheet, one-pager, samples, and demo kit" },
    { id: "cadence",   label: "Weekly / biweekly broker review cadence with measurable goals" },
  ],
  "retail-buyer-masterclass": [
    { id: "buyer-map", label: "Map every buyer for your target retailers by category" },
    { id: "pitch",     label: "Build a buyer-ready pitch deck (10 slides max)" },
    { id: "data",      label: "Bring at least one channel of real velocity data to every meeting" },
    { id: "story",     label: "Rehearse the 'why we increase your category' story" },
    { id: "followup",  label: "Send a written follow-up + updated data within 48 hours of every meeting" },
  ],
  "cogs-financials": [
    { id: "cogs-model",label: "Complete a full COGS build including freight, labor, waste, and pallets" },
    { id: "promo-math",label: "Model promo math — TPR lift + retailer margin + spoilage impact" },
    { id: "sku-cm",    label: "Compute contribution margin per SKU per channel" },
    { id: "scale",     label: "Model cost curves at 2x, 5x, 10x volume" },
    { id: "risk",      label: "Identify the top 3 COGS levers you'll pull to improve margin at scale" },
  ],
  "capital-raise": [
    { id: "story",     label: "Write your investor narrative (problem → why now → why you)" },
    { id: "deck",      label: "Build a 10-slide investor deck" },
    { id: "model",     label: "Build a 3-year financial model with 3 scenarios" },
    { id: "use-funds", label: "Define use of funds + milestones the raise unlocks" },
    { id: "safe",      label: "Prepare a SAFE (or convertible note) with realistic cap + discount" },
    { id: "list",      label: "Build a target investor list — 30 aligned angels/funds minimum" },
  ],
  "market-testing": [
    { id: "hypothesis",label: "Write your test hypothesis (audience × price × claim)" },
    { id: "footprint", label: "Select a small test footprint (10-40 stores or a defined region)" },
    { id: "kpi",       label: "Define pass/fail metrics BEFORE launch (velocity, repeat, GM)" },
    { id: "duration",  label: "Run the test for at least 90 days to see repeat behavior" },
    { id: "signal",    label: "Distinguish real signal (repeat) from noise (initial trial)" },
    { id: "next",      label: "Only expand geography when the whole KPI set — not just revenue — is green" },
  ],
};

/**
 * @returns {{id: string, label: string}[]} — array (empty if none defined)
 */
export function getChecklist(slug) {
  return MODULE_CHECKLISTS[slug] || [];
}
